import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { analytics } from "../../helpers/analytics";
import {
  trackFunnelStart,
  trackFunnelLead,
  trackFunnelUnlock,
  trackPaywallView,
  trackCheckoutStart,
  trackSubscriptionPurchase,
  trackStartTrial,
  trackStartFunnelCustomEvent,
} from "../../helpers/metaPixel";
import { formatAmount } from "../../helpers/shared";
import { SignIn } from "../../components/SignIn";
import { UserContext } from "../../context/userContext";
import { MontageFunnel } from "./MontageFunnel";
import { selectFunnelPrices, FunnelPrices } from "./prices";

import appIcon from "../../assets/images/funnel/app-icon.png";
import animeImg from "../../assets/images/funnel/anime_preference.webp";
import portraitImg from "../../assets/images/funnel/portrait_preference.webp";
import tattooImg from "../../assets/images/funnel/tattoo_preference.webp";
import letteringImg from "../../assets/images/funnel/lettering_preference.webp";
import textureImg from "../../assets/images/funnel/texture_preference.webp";
import traditionalImg from "../../assets/images/funnel/traditional_preference.webp";
import packGridImg from "../../assets/images/funnel/grid-items-paywall.webp";
import mayaAvatar from "../../assets/images/funnel/review-maya.webp";
import kenjiAvatar from "../../assets/images/funnel/review-kenji.webp";
import sofiaAvatar from "../../assets/images/funnel/review-sofia.webp";
import emmaAvatar from "../../assets/images/funnel/review-emma.webp";

/**
 * Web2App quiz funnel — "brush showcase" concept.
 * Flow: hook → quiz (5 steps) → building loader → email → blurred payoff →
 * in-house paywall → Stripe Checkout → success (Supabase magic link into the app).
 */

const PROFILE_ID_KEY = "funnel_external_profile_id";
const UTM_KEY = "funnel_entry_params";
const EMAIL_KEY = "funnel_email";
const STYLE_KEY = "funnel_style";
const OFFER_DEADLINE_KEY = "funnel_offer_deadline";
const OFFER_MINUTES = 15;

// Experiment: land directly on the first quiz question instead of the hook
// screen. PostHog flag returns "control" | "test"; anything else = control.
const FLAG_QUIZ_FIRST = "start-funnel-quiz-first";
// Funnel v2 experiment: swap the whole quiz funnel for the single-link montage
// showcase. Held at 0% rollout in PostHog until the quiz-first test reads and
// the showcase video is ready, so today this resolves to "control" for everyone.
const FLAG_FUNNEL_V2 = "start-funnel-v2";
// Paywall trial experiment: the trial arm offers a 3-day free trial on the
// annual plan ("try free, then $X/yr") instead of charging up front. Resolved
// once in the Start wrapper and passed to whichever funnel mounts, so both arms
// apply it identically.
const FLAG_PAYWALL_TRIAL = "paywall-trial";
// Kill-switch for the exit-intent downsell modal. Not an experiment — a boolean
// flag so we can turn the offer off instantly without a deploy. Held off until
// the Stripe discount coupon + STRIPE_EXIT_OFFER_COUPON env are in place, or the
// modal would promise a discount the checkout doesn't apply.
const FLAG_EXIT_OFFER = "paywall-exit-offer";
// Warm-up experiment: put questions → "building your pack" → email back between
// the reel and the payoff. Montage dropped all three and now sends three times
// as many people to the checkout button without moving revenue — the visitors it
// gains are ones who never intended to buy. Runs alongside the trial experiment:
// the two touch different stages and PostHog assigns them independently, so each
// still compares half the traffic against the other half.
const FLAG_FUNNEL_QUIZ = "start-funnel-quiz";
// Counting-down coupon on the paywall. What the clock grants is a free first
// day on the annual plan, not a discount — the price is $29.99 whether the
// visitor beats it or not, and when it expires the offer genuinely stops being
// made. Mutually exclusive with FLAG_PAYWALL_TRIAL: that one hands everyone
// three free days unconditionally, which would leave this countdown ticking
// down to something already given.
const FLAG_TIMER_OFFER = "paywall-timer-offer";
// A third of everyone who starts the quiz leaves on the first question, and it
// is the only question that waits on eight photographs before it means
// anything. The test arm draws it as emoji tiles like the other nine. Kept an
// experiment rather than a straight swap: the images are real library covers
// and might be doing work we can't see from the drop-off alone.
const FLAG_STYLE_EMOJI = "quiz-style-emoji";
// Both arms see the countdown announcement — what differs is how loudly. The
// test arm makes the clock the subject, brings it back once the pack exists,
// and shows it expiring rather than letting it vanish.
const FLAG_OFFER_INTRO_V2 = "paywall-offer-intro-v2";
// Payment form on our own page instead of a trip to checkout.stripe.com. Ninety
// people a day reach that redirect and a handful come back. Kept behind a flag
// so the hosted redirect can be restored instantly without a deploy — this is
// the one path in the funnel where a bug costs somebody money.
const FLAG_EMBEDDED_CHECKOUT = "checkout-embedded";
type FunnelVariant = "control" | "test";

const getOfferDeadline = (): number => {
  try {
    const stored = Number(localStorage.getItem(OFFER_DEADLINE_KEY));
    if (stored && stored > Date.now()) return stored;
    const deadline = Date.now() + OFFER_MINUTES * 60 * 1000;
    localStorage.setItem(OFFER_DEADLINE_KEY, String(deadline));
    return deadline;
  } catch {
    return Date.now() + OFFER_MINUTES * 60 * 1000;
  }
};

const REVIEWS = [
  {
    name: "Maya R.",
    detail: "Portrait artist",
    text: "The brushes completely changed my portraits. I finally stopped fighting with the defaults — worth every cent.",
    image: mayaAvatar,
  },
  {
    name: "Kenji T.",
    detail: "Anime & manga",
    text: "Best anime brush set I've found. My linework looks clean straight away, and new packs drop every week.",
    image: kenjiAvatar,
  },
  {
    name: "Sofia L.",
    detail: "Lettering",
    text: "I sold my first commission two weeks after getting the lettering pack. The Academy lessons helped a lot too.",
    image: sofiaAvatar,
  },
];

const FAQS = [
  {
    q: "How do I get the brushes into Procreate?",
    a: "Download any pack and tap it on your iPad — Procreate imports it automatically. Every pack works with Procreate 5 and newer.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Manage or cancel your subscription in one tap from your account — you keep access until the end of the paid period.",
  },
  {
    q: "What exactly is included?",
    a: "Your personalized pack plus unlimited downloads from the full library: 10,000+ brushes, fonts, color palettes, stickers, 3D assets and Academy courses, with new drops every week.",
  },
  {
    q: "Is there a money-back guarantee?",
    a: "Yes — if you're not happy within 30 days of purchase, contact support and we'll refund you in full.",
  },
  {
    q: "Does it work on the iOS app too?",
    a: "Yes. Sign in with the same email in the Tools for Procreate iOS app and premium unlocks there automatically.",
  },
];

const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getProfileId = (): string => {
  try {
    const existing = localStorage.getItem(PROFILE_ID_KEY);
    if (existing) return existing;
    const id = generateUUID();
    localStorage.setItem(PROFILE_ID_KEY, id);
    return id;
  } catch {
    return generateUUID();
  }
};

/** Persist ad params from the entry URL so they survive the whole quiz. */
const storeEntryParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const keep: Record<string, string> = {};
    [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
      "fbclid",
      "ad_id",
      "adset_id",
      "campaign_id",
    ].forEach((k) => {
      const v = params.get(k);
      if (v) keep[k] = v;
    });
    if (Object.keys(keep).length) {
      localStorage.setItem(UTM_KEY, JSON.stringify(keep));
    }
    return keep;
  } catch {
    return {};
  }
};

const getEntryParams = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(UTM_KEY) || "{}");
  } catch {
    return {};
  }
};

type StyleOption = { image: string; label: string; value: string };

const STYLE_OPTIONS: StyleOption[] = [
  { image: portraitImg, label: "Portraits & people", value: "portraits" },
  { image: animeImg, label: "Anime & manga", value: "anime" },
  { image: letteringImg, label: "Lettering", value: "lettering" },
  { image: traditionalImg, label: "Nature & illustration", value: "landscapes" },
  { image: tattooImg, label: "Tattoo & ink", value: "tattoo" },
  { image: textureImg, label: "A bit of everything", value: "everything" },
];

type QuizOption = { emoji: string; label: string; value: string };
type QuizQuestion = {
  id: string;
  title: string;
  subtitle?: string;
  options: QuizOption[];
};

const QUESTIONS: QuizQuestion[] = [
  {
    id: "style",
    title: "What do you love to draw?",
    subtitle: "We'll match brushes to your style",
    options: [], // rendered from STYLE_OPTIONS as image cards
  },
  {
    id: "level",
    title: "Your Procreate level?",
    options: [
      { emoji: "🌱", label: "Just getting started", value: "beginner" },
      { emoji: "✏️", label: "I draw for fun", value: "hobbyist" },
      { emoji: "💼", label: "Semi-pro / taking commissions", value: "semi_pro" },
      { emoji: "🏆", label: "Professional artist", value: "pro" },
    ],
  },
  {
    id: "struggle",
    title: "What's holding your art back?",
    options: [
      { emoji: "😐", label: "Default brushes look flat", value: "flat_brushes" },
      { emoji: "🔍", label: "Can't find the right brushes", value: "cant_find" },
      { emoji: "〰️", label: "My linework feels shaky", value: "linework" },
      { emoji: "🌗", label: "Coloring & shading", value: "shading" },
    ],
  },
  {
    id: "goal",
    title: "What's your goal?",
    options: [
      { emoji: "📸", label: "Create post-worthy art", value: "post_worthy" },
      { emoji: "💰", label: "Sell art & get commissions", value: "sell" },
      { emoji: "🧘", label: "Relax and enjoy drawing", value: "relax" },
      { emoji: "🚀", label: "Master Procreate", value: "master" },
    ],
  },
  {
    id: "time",
    title: "How much time can you draw per day?",
    options: [
      { emoji: "⚡", label: "5–10 minutes", value: "5_10" },
      { emoji: "☕", label: "20–30 minutes", value: "20_30" },
      { emoji: "🔥", label: "An hour or more", value: "60_plus" },
    ],
  },
];

const STYLE_IMAGES: Record<string, string> = {
  portraits: portraitImg,
  anime: animeImg,
  lettering: letteringImg,
  landscapes: traditionalImg,
  tattoo: tattooImg,
  everything: textureImg,
};

const PACK_NAMES: Record<string, { name: string; count: number }> = {
  portraits: { name: "Portrait Pro", count: 84 },
  anime: { name: "Anime & Manga", count: 92 },
  lettering: { name: "Lettering Master", count: 76 },
  landscapes: { name: "Illustration", count: 81 },
  tattoo: { name: "Tattoo Artist", count: 68 },
  everything: { name: "All-Style Starter", count: 120 },
};

const LOADING_STEPS = [
  "Analyzing your art style…",
  "Matching brushes from 10,000+ library…",
  "Adding courses for your goal…",
  "Curating your personal pack…",
];

const TESTIMONIALS = [
  {
    name: "Maya R.",
    text: "The brushes completely changed my portraits. I finally stopped fighting with the defaults.",
    style: "portraits",
    image: mayaAvatar,
  },
  {
    name: "Kenji T.",
    text: "Best anime brush set I've found — my linework looks clean straight away.",
    style: "anime",
    image: kenjiAvatar,
  },
  {
    name: "Sofia L.",
    text: "I sold my first commission two weeks after getting the lettering pack.",
    style: "lettering",
    image: sofiaAvatar,
  },
  {
    name: "Emma W.",
    text: "New brushes every week keeps me actually drawing every day.",
    style: "everything",
    image: emmaAvatar,
  },
];

const Stars = ({ size = 14 }: { size?: number }) => (
  <span
    className="text-[#FFB800] tracking-tight"
    style={{ fontSize: size }}
    aria-label="5 stars"
  >
    ★★★★★
  </span>
);

type Phase =
  | "hook"
  | "quiz"
  | "loading"
  | "email"
  | "payoff"
  | "paywall"
  | "success";

// Stripe returns to /subscription-activated for immediate purchases and
// /trial-activated when the checkout started a free trial. Treat both as the
// success screen; the trial path also tells us to log a trial start (a $0
// commitment) rather than a real purchase, so the two never get conflated.
const isSuccessPath = () =>
  typeof window !== "undefined" &&
  (window.location.pathname.includes("subscription-activated") ||
    window.location.pathname.includes("trial-activated"));

const isTrialSuccessPath = () =>
  typeof window !== "undefined" &&
  window.location.pathname.includes("trial-activated");

const ClassicFunnel = ({ trialArm = false }: { trialArm?: boolean }) => {
  const [phase, setPhase] = useState<Phase>(() =>
    isSuccessPath() ? "success" : "hook"
  );
  const [prices, setPrices] = useState<FunnelPrices>({});
  const [selectedPlan, setSelectedPlan] = useState<"annual" | "weekly">(
    "annual"
  );
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [offerSecondsLeft, setOfferSecondsLeft] = useState<number | null>(null);

  // Countdown for the "we're holding your pack" reservation timer.
  useEffect(() => {
    if (phase !== "paywall") return;
    const deadline = getOfferDeadline();
    const tick = () =>
      setOfferSecondsLeft(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  const offerTimeLabel = useMemo(() => {
    if (offerSecondsLeft === null) return "";
    const m = Math.floor(offerSecondsLeft / 60);
    const s = offerSecondsLeft % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [offerSecondsLeft]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const profileId = useRef(getProfileId());
  // Experiment variant + a gate so we don't render (or log) the funnel until
  // PostHog has resolved the flag — otherwise "test" users would flash the hook
  // screen before jumping to the quiz.
  const [variant, setVariant] = useState<FunnelVariant>("control");
  const [flagsReady, setFlagsReady] = useState(false);

  useEffect(() => {
    const entryParams = storeEntryParams();
    // Stripe redirects back into this page, which re-mounts it. Identify the
    // person so the purchase lands on the right profile, but don't log a second
    // funnel entry — the return URL carries no UTMs, so it used to register as
    // a fresh, untagged visitor that converted instantly.
    const returningFromCheckout = isSuccessPath();
    analytics.identify(profileId.current, {
      funnel: "start_brush_quiz",
      ...(returningFromCheckout ? getEntryParams() : entryParams),
    });

    // Resolve the experiment variant before logging the funnel entry, so every
    // event (starting with "Funnel Started") carries funnel_variant. If the flag
    // never resolves (adblock / opt-out / slow network past the timeout) the
    // visitor is entered UNASSIGNED — we render control UI so they aren't stuck,
    // but we do NOT stamp funnel_variant, so they stay out of the experiment
    // instead of inflating control. Forcing control here previously caused a
    // ~58/42 sample-ratio mismatch.
    let fired = false;
    const start = () => {
      if (fired) return;
      fired = true;

      const raw = analytics.getFeatureFlag(FLAG_QUIZ_FIRST);
      const assigned = raw === "test" || raw === "control";
      const v: FunnelVariant = raw === "test" ? "test" : "control";
      setVariant(v);
      // Only stamp the variant when the flag actually resolved; unassigned
      // visitors are excluded from the experiment rather than polluting control.
      if (assigned) {
        analytics.register({ funnel_variant: v });
      }

      if (!returningFromCheckout) {
        // Test variant skips the hook screen and lands on quiz question 1, so
        // arriving IS starting the quiz — fire both entry events together.
        if (v === "test") {
          setPhase("quiz");
        }
        analytics.track("Funnel Started", {
          funnel: "start_brush_quiz",
          external_profile_id: profileId.current,
          ...entryParams,
        });
        trackFunnelStart({
          external_profile_id: profileId.current,
          ...entryParams,
        });
        if (v === "test") {
          analytics.track("Funnel Quiz Started", { funnel: "start_brush_quiz" });
          trackStartFunnelCustomEvent("StartFunnelQuizStarted", {
            external_profile_id: profileId.current,
          });
        }
      }

      setFlagsReady(true);
    };

    const unsubscribe = analytics.onFeatureFlags(start);
    // Safety net only for visitors whose flags never resolve. 3s (was 1200ms)
    // gives slow mobile connections time to load the flag and enter the
    // experiment properly instead of timing out into the unassigned bucket.
    const timer = window.setTimeout(start, 3000);
    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, []);

  // PostHog: one "step viewed" event per screen — makes drop-off funnels trivial.
  // Gated on flagsReady so the "test" variant doesn't log a phantom hook step
  // before it swaps to the quiz.
  useEffect(() => {
    if (!flagsReady) return;
    analytics.track("Funnel Step Viewed", {
      funnel: "start_brush_quiz",
      phase,
      question: phase === "quiz" ? QUESTIONS[questionIndex].id : undefined,
      step:
        phase === "hook"
          ? 1
          : phase === "quiz"
          ? questionIndex + 2
          : phase === "loading"
          ? QUESTIONS.length + 2
          : phase === "email"
          ? QUESTIONS.length + 3
          : phase === "payoff"
          ? QUESTIONS.length + 4
          : phase === "paywall"
          ? QUESTIONS.length + 5
          : QUESTIONS.length + 6,
    });
  }, [phase, questionIndex, flagsReady]);

  // The payoff screen sits between email and paywall and had no named event of
  // its own, so its ~10% drop-off was invisible in funnels.
  useEffect(() => {
    if (phase !== "payoff") return;
    analytics.track("Funnel Payoff Viewed", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
    });
  }, [phase]);

  // Stripe prices for the in-house paywall.
  useEffect(() => {
    fetch("/.netlify/functions/get-products")
      .then((res) => res.json())
      .then((products) => setPrices(selectFunnelPrices(products)))
      .catch(() => {
        analytics.track("Funnel Prices Failed", { funnel: "start_brush_quiz" });
      });
  }, []);

  const question = QUESTIONS[questionIndex];
  const progress =
    phase === "hook"
      ? 0
      : phase === "quiz"
      ? ((questionIndex + 1) / (QUESTIONS.length + 2)) * 100
      : phase === "loading"
      ? 82
      : phase === "email"
      ? 92
      : 100;

  const styleValue = useMemo(() => {
    if (answers.style) return answers.style;
    try {
      return localStorage.getItem(STYLE_KEY) || "";
    } catch {
      return "";
    }
  }, [answers.style]);

  const pack = useMemo(() => {
    return PACK_NAMES[styleValue] || PACK_NAMES.everything;
  }, [styleValue]);

  const storedEmail = useMemo(() => {
    if (email) return email;
    try {
      return localStorage.getItem(EMAIL_KEY) || "";
    } catch {
      return "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, phase]);

  const annualSavings = useMemo(() => {
    if (!prices.weekly?.amount || !prices.annual?.amount) return null;
    const savings = Math.round(
      (1 - prices.annual.amount / (prices.weekly.amount * 52)) * 100
    );
    return savings > 0 ? savings : null;
  }, [prices]);

  const testimonial = useMemo(() => {
    return (
      TESTIMONIALS.find((t) => t.style === styleValue) || TESTIMONIALS[3]
    );
  }, [styleValue]);

  const handleStartQuiz = () => {
    analytics.track("Funnel Quiz Started", { funnel: "start_brush_quiz" });
    trackStartFunnelCustomEvent("StartFunnelQuizStarted", {
      external_profile_id: profileId.current,
    });
    setPhase("quiz");
  };

  const handleSelect = useCallback(
    (value: string) => {
      if (selected) return; // prevent double taps during transition
      setSelected(value);
      const q = QUESTIONS[questionIndex];
      analytics.track("Funnel Quiz Answer", {
        funnel: "start_brush_quiz",
        question: q.id,
        answer: value,
        step: questionIndex + 1,
      });
      trackStartFunnelCustomEvent("StartFunnelQuizAnswered", {
        external_profile_id: profileId.current,
        question: q.id,
        answer: value,
        step: questionIndex + 1,
      });

      if (q.id === "style") {
        try {
          localStorage.setItem(STYLE_KEY, value);
        } catch {}
      }

      setTimeout(() => {
        setAnswers((prev) => ({ ...prev, [q.id]: value }));
        setSelected(null);
        if (questionIndex + 1 < QUESTIONS.length) {
          setQuestionIndex(questionIndex + 1);
        } else {
          setPhase("loading");
        }
      }, 280);
    },
    [questionIndex, selected]
  );

  const handleBack = () => {
    if (questionIndex > 0) {
      setQuestionIndex(questionIndex - 1);
    } else if (variant !== "test") {
      // Test variant has no hook screen to go back to.
      setPhase("hook");
    }
  };

  // Animated "building your pack" loader.
  useEffect(() => {
    if (phase !== "loading") return;
    setLoadingStep(0);
    const timers: ReturnType<typeof setTimeout>[] = [];
    LOADING_STEPS.forEach((_, i) => {
      timers.push(setTimeout(() => setLoadingStep(i + 1), (i + 1) * 900));
    });
    timers.push(
      setTimeout(() => {
        analytics.track("Funnel Loading Completed", {
          funnel: "start_brush_quiz",
        });
        trackStartFunnelCustomEvent("StartFunnelQuizCompleted", {
          external_profile_id: profileId.current,
          style: answers.style,
          level: answers.level,
          struggle: answers.struggle,
          goal: answers.goal,
          time: answers.time,
        });
        setPhase("email");
      }, LOADING_STEPS.length * 900 + 600)
    );
    return () => timers.forEach(clearTimeout);
  }, [answers, phase]);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setEmailError("Please enter a valid email");
      return;
    }
    setEmailError("");
    try {
      localStorage.setItem(EMAIL_KEY, trimmed);
    } catch {}
    analytics.identify(profileId.current, { email: trimmed, ...answers });
    // Same join as in the montage funnel: without it the buyer and the signed-in
    // user are two unrelated people and the trail ends at the payment.
    analytics.alias(trimmed);
    analytics.track("Funnel Email Submitted", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
    });
    trackFunnelLead({
      external_profile_id: profileId.current,
      style: answers.style,
      level: answers.level,
      struggle: answers.struggle,
      goal: answers.goal,
      time: answers.time,
    });

    // Create the Adapty profile server-side (fire-and-forget — never blocks the funnel).
    fetch("/.netlify/functions/create-funnel-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        email: trimmed,
        profileId: profileId.current,
        answers,
        utm: getEntryParams(),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        analytics.track("Funnel Adapty Profile Created", {
          funnel: "start_brush_quiz",
          external_profile_id: profileId.current,
          ok: Boolean(data?.ok),
        });
      })
      .catch(() => {
        analytics.track("Funnel Adapty Profile Failed", {
          funnel: "start_brush_quiz",
          external_profile_id: profileId.current,
        });
      });

    setPhase("payoff");
  };

  const handleUnlock = () => {
    trackFunnelUnlock({
      external_profile_id: profileId.current,
      pack_name: pack.name,
      pack_count: pack.count,
      style: styleValue,
    });
    trackPaywallView({ source: "start_brush_quiz" });
    trackStartFunnelCustomEvent("StartFunnelPaywallViewed", {
      external_profile_id: profileId.current,
      pack_name: pack.name,
      pack_count: pack.count,
      style: styleValue,
    });
    analytics.track("Funnel Paywall Viewed", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
    });
    setPhase("paywall");
  };

  // Trial only applies to the annual plan — a free trial on a weekly sub makes
  // no sense (it would just be a delayed first week).
  const useTrial = trialArm && selectedPlan === "annual";

  const handleCheckout = async () => {
    const plan = selectedPlan === "annual" ? prices.annual : prices.weekly;
    if (!plan || checkoutLoading) return;

    setCheckoutLoading(true);
    setCheckoutError("");
    analytics.track("Funnel Checkout Started", {
      funnel: "start_brush_quiz",
      plan: selectedPlan,
      amount: plan.amount,
      is_trial: useTrial,
      external_profile_id: profileId.current,
    });
    trackCheckoutStart({
      plan: `funnel_${selectedPlan}_subscription`,
      amount: plan.amount,
    });
    trackStartFunnelCustomEvent("StartFunnelCheckoutStarted", {
      external_profile_id: profileId.current,
      plan: selectedPlan,
      amount: plan.amount,
      value: plan.amount / 100,
      currency: "USD",
      pack_name: pack.name,
      style: styleValue,
    });
    try {
      localStorage.setItem("last_purchase", String(plan.amount / 100));
    } catch {}

    try {
      const response = await fetch("/.netlify/functions/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sku: plan.priceId,
          ...(useTrial && { hasTrial: true }),
          userId: profileId.current,
          email: storedEmail,
          source: "start_brush_quiz",
          variationId: `start_brush_quiz_${selectedPlan}${useTrial ? "_trial" : ""}`,
        }),
      }).then((res) => res.json());

      if (response.url) {
        analytics.track("Funnel Checkout Created", {
          funnel: "start_brush_quiz",
          plan: selectedPlan,
        });
        trackStartFunnelCustomEvent("StartFunnelCheckoutCreated", {
          external_profile_id: profileId.current,
          plan: selectedPlan,
          amount: plan.amount,
          value: plan.amount / 100,
          currency: "USD",
        });
        window.location.assign(response.url);
        return;
      }

      throw new Error(response.error || "Checkout response missing url");
    } catch (error) {
      analytics.track("Funnel Checkout Failed", {
        funnel: "start_brush_quiz",
        plan: selectedPlan,
        error_message: error instanceof Error ? error.message : String(error),
      });
      trackStartFunnelCustomEvent("StartFunnelCheckoutFailed", {
        external_profile_id: profileId.current,
        plan: selectedPlan,
        error_message: error instanceof Error ? error.message : String(error),
      });
      setCheckoutError("Something went wrong — please try again.");
      setCheckoutLoading(false);
    }
  };

  // Post-purchase success: fire purchase pixel once + open the Supabase sign-in window.
  const successHandled = useRef(false);
  useEffect(() => {
    if (phase !== "success" || successHandled.current) return;
    successHandled.current = true;

    const trialActivated = isTrialSuccessPath();

    // Comparable across both arms: "made it through Stripe / committed." This is
    // the trial experiment's primary metric — trial starts and real purchases
    // both count here.
    analytics.track("Funnel Checkout Completed", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
      plan: selectedPlan,
      is_trial: trialActivated,
    });

    // Keep the two payoffs distinct: a trial start is a $0 commitment, a real
    // purchase is money now. Never let a trial inflate the purchase metric.
    if (trialActivated) {
      analytics.track("Funnel Trial Started", {
        funnel: "start_brush_quiz",
        external_profile_id: profileId.current,
        plan: selectedPlan,
      });
    } else {
      analytics.track("Funnel Purchase Completed", {
        funnel: "start_brush_quiz",
        external_profile_id: profileId.current,
        plan: selectedPlan,
      });
    }

    let alreadyTracked = false;
    try {
      alreadyTracked = sessionStorage.getItem("funnel_purchase_tracked") === "1";
      sessionStorage.setItem("funnel_purchase_tracked", "1");
    } catch {}

    if (!alreadyTracked) {
      let amount: string | null = null;
      try {
        amount = localStorage.getItem("last_purchase");
      } catch {}
      if (trialActivated) {
        // Standard StartTrial (optimizable) + our granular custom event — the
        // trial mirror of pay-now's Purchase. The real Purchase fires only on the
        // actual charge (server-side CAPI on invoice.paid, fast-follow), so ads
        // are never told "purchased" for a $0 trial.
        trackStartTrial(amount);
        trackStartFunnelCustomEvent("StartFunnelTrialStarted", {
          external_profile_id: profileId.current,
          plan: selectedPlan,
          value: amount ? Number(amount) : undefined,
          currency: "USD",
        });
      } else {
        trackStartFunnelCustomEvent("StartFunnelPurchaseCompleted", {
          external_profile_id: profileId.current,
          plan: selectedPlan,
          value: amount ? Number(amount) : undefined,
          currency: "USD",
        });
        trackSubscriptionPurchase(amount, "funnel_subscription");
      }
    }

    setAuthOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const handleAuthSuccess = () => {
    setAuthOpen(false);
    analytics.track("Funnel Auth Completed", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
    });
    trackStartFunnelCustomEvent("StartFunnelAuthCompleted", {
      external_profile_id: profileId.current,
    });
    window.location.assign("/");
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      {/* Top bar: logo + progress */}
      <div className="w-full px-5 pt-5 pb-3 max-w-md mx-auto">
        <div className="flex items-center justify-between mb-4">
          {phase === "quiz" && !(variant === "test" && questionIndex === 0) ? (
            <button
              onClick={handleBack}
              aria-label="Back"
              className="text-main-grey-800 text-xl leading-none p-1"
            >
              ←
            </button>
          ) : (
            <span className="w-6" />
          )}
          <span className="flex items-center gap-2">
            <img
              src={appIcon}
              alt="Tools for Procreate"
              className="w-7 h-7 rounded-lg"
            />
            <span className="font-bold text-black text-lg tracking-tight">
              Tools <span className="text-main-blue">for Procreate</span>
            </span>
          </span>
          <span className="w-6" />
        </div>
        <div className="h-1.5 w-full bg-main-grey-300 rounded-full overflow-hidden">
          <div
            className="h-full bg-main-blue rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex-1 w-full max-w-md mx-auto px-5 pb-10 flex flex-col">
        {!flagsReady && phase !== "success" && (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-main-grey-300 border-t-main-blue rounded-full animate-spin" />
          </div>
        )}
        {flagsReady && phase === "hook" && (
          <div className="flex-1 flex flex-col justify-center text-center">
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[animeImg, portraitImg, tattooImg, letteringImg, traditionalImg, textureImg].map(
                (img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt=""
                    loading={i > 2 ? "lazy" : undefined}
                    className="h-24 w-full object-cover rounded-2xl"
                  />
                )
              )}
            </div>
            <h1 className="text-3xl font-bold text-black leading-tight mb-3">
              Find the perfect brushes for{" "}
              <span className="text-main-blue">your</span> art style
            </h1>
            <p className="text-main-grey-800 text-sm mb-5">
              Take a 1-minute quiz and get a personalized pack from our library
              of 10,000+ Procreate brushes, fonts & courses.
            </p>

            {/* Social proof: stats bar */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none">1M+</p>
                <p className="text-main-grey-800 text-xxs mt-1">artists</p>
              </div>
              <div className="w-px h-8 bg-main-grey-300" />
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none">
                  10,000+
                </p>
                <p className="text-main-grey-800 text-xxs mt-1">brushes</p>
              </div>
              <div className="w-px h-8 bg-main-grey-300" />
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none flex items-center gap-1 justify-center">
                  4.7 <span className="text-[#FFB800] text-sm">★</span>
                </p>
                <p className="text-main-grey-800 text-xxs mt-1">App Store</p>
              </div>
            </div>

            <button
              onClick={handleStartQuiz}
              className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
            >
              Get My Brushes
            </button>
            <p className="text-main-grey-800 text-xxs mt-4">
              Loved by Procreate artists in 130+ countries
            </p>
          </div>
        )}

        {flagsReady && phase === "quiz" && (
          <div className="flex-1 flex flex-col pt-6">
            <h2 className="text-2xl font-bold text-black leading-tight mb-1">
              {question.title}
            </h2>
            {question.subtitle && (
              <p className="text-main-grey-800 text-sm mb-4">
                {question.subtitle}
              </p>
            )}

            {question.id === "style" ? (
              <div className="mt-4 grid grid-cols-2 gap-3">
                {STYLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`rounded-2xl overflow-hidden border-2 text-left transition-colors ${
                      selected === opt.value
                        ? "border-main-blue"
                        : "border-main-grey-300 active:border-main-grey-400"
                    }`}
                  >
                    <img
                      src={opt.image}
                      alt={opt.label}
                      className="w-full h-28 object-cover"
                    />
                    <span
                      className={`block px-3 py-2 text-xs font-medium ${
                        selected === opt.value
                          ? "bg-light-blue text-main-blue"
                          : "bg-white text-black"
                      }`}
                    >
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {question.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`w-full flex items-center gap-3 border-2 rounded-2xl px-4 py-4 text-left text-black text-sm font-medium transition-colors ${
                      selected === opt.value
                        ? "border-main-blue bg-light-blue"
                        : "border-main-grey-300 bg-white active:bg-main-grey-200"
                    }`}
                  >
                    <span className="text-2xl">{opt.emoji}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Social proof: rotating testimonial under quiz */}
            <div className="mt-auto pt-6">
              <div className="bg-main-grey-200 rounded-2xl px-4 py-3 flex items-start gap-3">
                <img
                  src={TESTIMONIALS[questionIndex % TESTIMONIALS.length].image}
                  alt={`${TESTIMONIALS[questionIndex % TESTIMONIALS.length].name} review`}
                  className="w-8 h-8 rounded-full object-cover shrink-0"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-black text-xxs font-semibold">
                      {TESTIMONIALS[questionIndex % TESTIMONIALS.length].name}
                    </span>
                    <Stars size={11} />
                  </div>
                  <p className="text-main-grey-800 text-xxs mt-0.5 leading-snug">
                    "{TESTIMONIALS[questionIndex % TESTIMONIALS.length].text}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {phase === "loading" && (
          <div className="flex-1 flex flex-col justify-center items-center text-center">
            <div className="w-14 h-14 border-4 border-main-grey-300 border-t-main-blue rounded-full animate-spin mb-8" />
            <h2 className="text-2xl font-bold text-black mb-6">
              Building your pack…
            </h2>
            <div className="space-y-3 text-left">
              {LOADING_STEPS.map((step, i) => (
                <div
                  key={step}
                  className={`flex items-center gap-2 text-sm transition-opacity duration-300 ${
                    loadingStep > i
                      ? "text-black opacity-100"
                      : "text-main-grey-800 opacity-40"
                  }`}
                >
                  <span>{loadingStep > i ? "✅" : "⏳"}</span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
            <p className="text-main-grey-800 text-xxs mt-8">
              2,384 artists took this quiz in the last 24 hours
            </p>
          </div>
        )}

        {phase === "email" && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="relative mx-auto mb-6">
              <img
                src={STYLE_IMAGES[answers.style] || textureImg}
                alt=""
                className="w-44 h-44 object-cover rounded-3xl shadow-lg"
              />
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-main-blue text-white text-xxs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                ✓ Pack curated
              </div>
            </div>
            <h2 className="text-2xl font-bold text-black leading-tight mb-2 text-center">
              Your <span className="text-main-blue">{pack.name}</span> pack is
              ready 🎉
            </h2>
            <p className="text-main-grey-800 text-sm text-center mb-6">
              Enter your email to save your personalized pack and get it on all
              your devices.
            </p>
            <form onSubmit={handleEmailSubmit} noValidate>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-2 border-main-grey-300 focus:border-main-blue outline-none rounded-2xl px-4 py-4 text-black text-sm mb-2"
              />
              {emailError && (
                <p className="text-main-red-900 text-xxs mb-2">{emailError}</p>
              )}
              <button
                type="submit"
                className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg mt-2 active:scale-[0.98] transition-transform"
              >
                See my pack
              </button>
            </form>
            <div className="flex items-center justify-center gap-2 mt-5">
              <div className="flex -space-x-2">
                {["M", "K", "S", "E"].map((c, i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full bg-light-blue text-main-blue border-2 border-white flex items-center justify-center text-xxs font-bold"
                  >
                    {c}
                  </div>
                ))}
              </div>
              <p className="text-main-grey-800 text-xxs">
                Join 1M+ artists already creating with Tools
              </p>
            </div>
            <p className="text-main-grey-800 text-xxs text-center mt-3">
              🔒 We respect your privacy. No spam, ever.
            </p>
          </div>
        )}

        {phase === "payoff" && (
          <div className="flex-1 flex flex-col pt-4">
            <h2 className="text-2xl font-bold text-black leading-tight mb-1 text-center">
              {pack.count} brushes matched your style
            </h2>
            <p className="text-main-grey-800 text-sm text-center mb-5">
              Your personal <b>{pack.name}</b> pack — curated for your level and
              goal.
            </p>

            {/* Blurred pack grid — the payoff hook (real pack art from the app) */}
            <div className="relative rounded-3xl overflow-hidden border border-main-grey-300">
              <img
                src={packGridImg}
                alt=""
                className="w-full h-56 object-cover blur-md select-none pointer-events-none scale-105"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/30">
                <div className="w-12 h-12 rounded-full bg-main-blue text-white flex items-center justify-center text-xl shadow-lg mb-2">
                  🔒
                </div>
                <span className="bg-white/90 text-black text-xxs font-semibold px-3 py-1 rounded-full">
                  Unlock to reveal your pack
                </span>
              </div>
            </div>

            <div className="mt-5 space-y-2 text-sm text-black">
              <p>✅ {pack.count} hand-picked brushes for your style</p>
              <p>✅ Fonts, color palettes & stickers included</p>
              <p>✅ Step-by-step courses for your goal</p>
              <p>✅ New drops every week</p>
            </div>

            {/* Social proof: matched testimonial + rating */}
            <div className="mt-4 bg-main-grey-200 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <img
                  src={testimonial.image}
                  alt={`${testimonial.name} review`}
                  className="w-7 h-7 rounded-full object-cover shrink-0"
                />
                <span className="text-black text-xxs font-semibold">
                  {testimonial.name}
                </span>
                <Stars size={11} />
              </div>
              <p className="text-main-grey-800 text-xxs leading-snug">
                "{testimonial.text}"
              </p>
            </div>

            <button
              onClick={handleUnlock}
              className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg mt-5 active:scale-[0.98] transition-transform"
            >
              Unlock my pack
            </button>
            <p className="text-main-grey-800 text-xxs text-center mt-3">
              ★ 4.7 on the App Store · 1M+ artists · 130+ countries
            </p>
          </div>
        )}

        {phase === "paywall" && (
          <div className="flex-1 flex flex-col pt-2 pb-24">
            {/* Reservation timer */}
            <div className="sticky top-0 z-10 -mx-5 px-5 py-2.5 bg-main-blue text-white flex items-center justify-center gap-2 text-sm font-semibold">
              <span>⏳</span>
              <span>
                {offerSecondsLeft === 0
                  ? "Your pack is still waiting for you"
                  : `We're holding your ${pack.name} pack for`}
              </span>
              {offerSecondsLeft !== 0 && (
                <span className="bg-white/20 rounded-lg px-2 py-0.5 font-mono tabular-nums">
                  {offerTimeLabel || "15:00"}
                </span>
              )}
            </div>

            <h2 className="text-2xl font-bold text-black leading-tight mb-1 text-center mt-4">
              Unlock your <span className="text-main-blue">{pack.name}</span>{" "}
              pack
            </h2>
            <p className="text-main-grey-800 text-sm text-center mb-4">
              Plus full access to 10,000+ brushes, fonts, courses & weekly
              drops.
            </p>

            {/* Blurred pack reminder */}
            <div className="relative rounded-2xl overflow-hidden border border-main-grey-300 mb-5">
              <img
                src={packGridImg}
                alt=""
                className="w-full h-24 object-cover blur-sm select-none pointer-events-none scale-105"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-white/25">
                <span className="bg-white/90 text-black text-xxs font-semibold px-3 py-1 rounded-full">
                  🔒 {pack.count} brushes matched your style
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setSelectedPlan("annual")}
                className={`w-full rounded-2xl border-2 px-4 py-4 text-left transition-colors ${
                  selectedPlan === "annual"
                    ? "border-main-blue bg-light-blue"
                    : "border-main-grey-300 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-black text-sm font-bold">
                      Annual
                    </span>
                    {trialArm ? (
                      <span className="ml-2 bg-main-blue text-white text-xxs font-bold px-2 py-0.5 rounded-full">
                        3 DAYS FREE
                      </span>
                    ) : (
                      annualSavings && (
                        <span className="ml-2 bg-main-blue text-white text-xxs font-bold px-2 py-0.5 rounded-full">
                          SAVE {annualSavings}%
                        </span>
                      )
                    )}
                    <p className="text-main-grey-800 text-xxs mt-1">
                      {trialArm
                        ? prices.annual
                          ? `3 days free, then ${formatAmount(prices.annual.amount)}/yr — cancel anytime`
                          : "3 days free"
                        : prices.annual
                        ? `${formatAmount(prices.annual.amount / 52)} / week, billed annually`
                        : "Best value"}
                    </p>
                  </div>
                  <span className="text-black font-bold text-lg">
                    {prices.annual ? formatAmount(prices.annual.amount) : "—"}
                  </span>
                </div>
              </button>

              <button
                onClick={() => setSelectedPlan("weekly")}
                className={`w-full rounded-2xl border-2 px-4 py-4 text-left transition-colors ${
                  selectedPlan === "weekly"
                    ? "border-main-blue bg-light-blue"
                    : "border-main-grey-300 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-black text-sm font-bold">
                      Weekly
                    </span>
                    <p className="text-main-grey-800 text-xxs mt-1">
                      Cancel anytime
                    </p>
                  </div>
                  <span className="text-black font-bold text-lg">
                    {prices.weekly ? formatAmount(prices.weekly.amount) : "—"}
                  </span>
                </div>
              </button>
            </div>

            <div className="mt-4 space-y-2 text-sm text-black">
              <p>✅ Your {pack.count}-brush {pack.name} pack, unlocked</p>
              <p>✅ Unlimited premium downloads</p>
              <p>✅ Academy courses & workflow guides</p>
              <p>✅ New drops every week</p>
            </div>

            {/* Money-back guarantee */}
            <div className="mt-5 border-2 border-main-grey-300 rounded-2xl px-4 py-4 flex items-start gap-3">
              <span className="text-2xl">🛡️</span>
              <div>
                <p className="text-black text-sm font-bold">
                  30-day money-back guarantee
                </p>
                <p className="text-main-grey-800 text-xxs mt-1 leading-snug">
                  If you're not happy with your pack, get a full refund within
                  30 days — no questions asked.
                </p>
              </div>
            </div>

            {/* Social proof: stats strip */}
            <div className="flex items-center justify-center gap-4 mt-5">
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none">1M+</p>
                <p className="text-main-grey-800 text-xxs mt-1">artists</p>
              </div>
              <div className="w-px h-8 bg-main-grey-300" />
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none flex items-center gap-1 justify-center">
                  4.7 <span className="text-[#FFB800] text-sm">★</span>
                </p>
                <p className="text-main-grey-800 text-xxs mt-1">App Store</p>
              </div>
              <div className="w-px h-8 bg-main-grey-300" />
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none">130+</p>
                <p className="text-main-grey-800 text-xxs mt-1">countries</p>
              </div>
            </div>

            {/* Reviews */}
            <h3 className="text-black font-bold text-lg mt-6 mb-3">
              What artists say
            </h3>
            <div className="space-y-3">
              {REVIEWS.map((review) => (
                <div
                  key={review.name}
                  className="bg-main-grey-200 rounded-2xl px-4 py-3"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <img
                      src={review.image}
                      alt={`${review.name} review`}
                      className="w-7 h-7 rounded-full object-cover shrink-0"
                    />
                    <div>
                      <span className="text-black text-xxs font-semibold block leading-none">
                        {review.name}
                      </span>
                      <span className="text-main-grey-800 text-xxs">
                        {review.detail} · Verified purchase
                      </span>
                    </div>
                    <span className="ml-auto">
                      <Stars size={11} />
                    </span>
                  </div>
                  <p className="text-main-grey-800 text-xxs leading-snug">
                    "{review.text}"
                  </p>
                </div>
              ))}
            </div>

            {/* FAQ */}
            <h3 className="text-black font-bold text-lg mt-6 mb-3">
              Frequently asked questions
            </h3>
            <div className="space-y-2">
              {FAQS.map((faq) => (
                <details
                  key={faq.q}
                  className="border-2 border-main-grey-300 rounded-2xl px-4 py-3 group"
                  onToggle={(e) => {
                    if ((e.target as HTMLDetailsElement).open) {
                      analytics.track("Funnel FAQ Opened", {
                        funnel: "start_brush_quiz",
                        question: faq.q,
                      });
                    }
                  }}
                >
                  <summary className="text-black text-sm font-semibold cursor-pointer list-none flex items-center justify-between">
                    <span>{faq.q}</span>
                    <span className="text-main-grey-800 transition-transform group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="text-main-grey-800 text-xs mt-2 leading-snug">
                    {faq.a}
                  </p>
                </details>
              ))}
            </div>

            {checkoutError && (
              <p className="text-main-red-900 text-xxs text-center mt-3">
                {checkoutError}
              </p>
            )}

            {/* Sticky checkout bar */}
            <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-main-grey-300 px-5 pt-3 pb-4">
              <div className="max-w-md mx-auto">
                <button
                  onClick={handleCheckout}
                  disabled={
                    checkoutLoading ||
                    !(selectedPlan === "annual" ? prices.annual : prices.weekly)
                  }
                  className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform disabled:opacity-70"
                >
                  {checkoutLoading
                    ? "Opening secure checkout…"
                    : useTrial
                    ? "Start my 3-day free trial"
                    : "Get My Brushes"}
                </button>
                <p className="text-main-grey-800 text-xxs text-center mt-2">
                  {useTrial && prices.annual
                    ? `First 3 days free, then ${formatAmount(
                        prices.annual.amount
                      )}/year · cancel anytime`
                    : "🔒 Secure payment via Stripe · Cancel anytime · 30-day guarantee"}
                </p>
              </div>
            </div>

            <p className="text-main-grey-800 text-xxs text-center mt-4">
              <a
                className="underline"
                href="https://endurable-weaver-d97.notion.site/Terms-Conditions-Tools-fbdcabcb12d942948055c022c3cf226b"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>{" "}
              &{" "}
              <a
                className="underline"
                href="https://endurable-weaver-d97.notion.site/Privacy-Policy-Tools-Products-87ad109325ae476a903172a44f41b393"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </p>
          </div>
        )}

        {phase === "success" && (
          <div className="flex-1 flex flex-col justify-center text-center">
            <div className="w-16 h-16 rounded-full bg-light-blue text-main-blue flex items-center justify-center text-3xl mx-auto mb-5">
              🎉
            </div>
            <h2 className="text-2xl font-bold text-black leading-tight mb-2">
              Payment confirmed — your pack is unlocked
            </h2>
            <p className="text-main-grey-800 text-sm mb-6">
              Sign in with{" "}
              <b className="text-black">{storedEmail || "the email you used at checkout"}</b>{" "}
              to open your library. Use the same email in the iOS app to unlock
              premium there too.
            </p>

            <button
              onClick={() => {
                analytics.track("Funnel Sign In Opened", {
                  funnel: "start_brush_quiz",
                });
                setAuthOpen(true);
              }}
              className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
            >
              Sign in to unlock
            </button>
            <a
              href="/"
              className="block w-full border-2 border-main-grey-300 text-black font-semibold py-4 rounded-2xl text-lg mt-3"
            >
              Browse the library
            </a>
          </div>
        )}
      </div>

      {phase === "success" && (
        <UserContext.Provider value={{ user: null }}>
          <SignIn
            isOpen={isAuthOpen}
            onClose={() => setAuthOpen(false)}
            onSuccess={handleAuthSuccess}
            initialEmail={storedEmail}
            isEmailLocked={Boolean(storedEmail)}
            hideGoogle
          />
        </UserContext.Provider>
      )}
    </div>
  );
};

// Resolves the funnel-v2 flag once, then mounts exactly one funnel so neither
// arm fires its entry events until we know which to show. Held at 0% rollout,
// so today everyone falls through to the classic funnel.
const Start = () => {
  const [funnel, setFunnel] = useState<"classic" | "montage" | null>(null);
  // Paywall trial arm, resolved alongside the funnel-v2 flag so both funnels get
  // the same value from one place. Independent of which funnel shows.
  const [trialArm, setTrialArm] = useState(false);
  const [exitOfferEnabled, setExitOfferEnabled] = useState(false);
  const [withQuiz, setWithQuiz] = useState(false);
  const [timerOffer, setTimerOffer] = useState(false);
  const [styleEmoji, setStyleEmoji] = useState(false);
  const [offerIntroArm, setOfferIntroArm] = useState<"control" | "test" | "late">("control");
  const [embeddedCheckout, setEmbeddedCheckout] = useState(false);

  useEffect(() => {
    let decided = false;
    const decide = () => {
      if (decided) return;
      decided = true;
      // ?funnel=montage|classic pins the funnel for QA. Without it a local run
      // whose flags are slow to arrive silently falls back to the classic
      // funnel, which makes checking the montage arm a coin toss.
      const forcedFunnel = new URLSearchParams(window.location.search).get("funnel");
      if (forcedFunnel === "montage" || forcedFunnel === "classic") {
        setFunnel(forcedFunnel);
      } else {
        const raw = analytics.getFeatureFlag(FLAG_FUNNEL_V2);
        // Only stamp the arm when the flag actually resolved — unresolved visitors
        // fall through to the classic funnel without polluting either arm.
        if (raw === "test" || raw === "control") {
          analytics.register({ funnel_v2_variant: raw });
        }
        setFunnel(raw === "test" ? "montage" : "classic");
      }

      // ?trial=1 / ?trial=0 forces the paywall arm for QA/preview without
      // touching experiment assignment (no variant is stamped), so we can eyeball
      // both arms directly. Absent → the real flag decides.
      const forcedTrial = new URLSearchParams(window.location.search).get("trial");
      if (forcedTrial === "1" || forcedTrial === "0") {
        setTrialArm(forcedTrial === "1");
      } else {
        const trialRaw = analytics.getFeatureFlag(FLAG_PAYWALL_TRIAL);
        if (trialRaw === "test" || trialRaw === "control") {
          analytics.register({ paywall_trial_variant: trialRaw });
        }
        setTrialArm(trialRaw === "test");
      }

      // Exit-offer kill switch. ?exit=1/0 forces it for QA/preview; otherwise the
      // boolean flag decides (on only when explicitly true, so an unresolved flag
      // stays safely off).
      const params = new URLSearchParams(window.location.search);
      const forcedExit = params.get("exit");
      if (forcedExit === "1" || forcedExit === "0") {
        setExitOfferEnabled(forcedExit === "1");
      } else {
        setExitOfferEnabled(analytics.getFeatureFlag(FLAG_EXIT_OFFER) === true);
      }

      // ?quiz=1 / ?quiz=0 forces the warm-up for QA without joining the experiment.
      const forcedQuiz = params.get("quiz");
      if (forcedQuiz === "1" || forcedQuiz === "0") {
        setWithQuiz(forcedQuiz === "1");
      } else {
        const quizRaw = analytics.getFeatureFlag(FLAG_FUNNEL_QUIZ);
        if (quizRaw === "test" || quizRaw === "control") {
          analytics.register({ funnel_quiz_variant: quizRaw });
        }
        setWithQuiz(quizRaw === "test");
      }

      // ?timer=1 / ?timer=0 forces the countdown coupon for QA.
      const forcedTimer = params.get("timer");
      if (forcedTimer === "1" || forcedTimer === "0") {
        setTimerOffer(forcedTimer === "1");
      } else {
        setTimerOffer(analytics.getFeatureFlag(FLAG_TIMER_OFFER) === true);
      }

      // ?emoji=1 / ?emoji=0 forces the first-question layout for QA.
      const forcedEmoji = params.get("emoji");
      if (forcedEmoji === "1" || forcedEmoji === "0") {
        setStyleEmoji(forcedEmoji === "1");
      } else {
        const emojiRaw = analytics.getFeatureFlag(FLAG_STYLE_EMOJI);
        if (emojiRaw === "test" || emojiRaw === "control") {
          analytics.register({ quiz_style_variant: emojiRaw });
        }
        setStyleEmoji(emojiRaw === "test");
      }

      // ?offer2=control|test|late forces the announcement arm for QA.
      const forcedOffer = params.get("offer2");
      if (forcedOffer === "control" || forcedOffer === "test" || forcedOffer === "late") {
        setOfferIntroArm(forcedOffer);
      } else {
        const offerRaw = analytics.getFeatureFlag(FLAG_OFFER_INTRO_V2);
        if (offerRaw === "test" || offerRaw === "control" || offerRaw === "late") {
          analytics.register({ offer_intro_variant: offerRaw });
          setOfferIntroArm(offerRaw);
        }
      }

      // ?embed=1 / ?embed=0 forces the checkout mode for QA.
      const forcedEmbed = params.get("embed");
      if (forcedEmbed === "1" || forcedEmbed === "0") {
        setEmbeddedCheckout(forcedEmbed === "1");
      } else {
        const embedRaw = analytics.getFeatureFlag(FLAG_EMBEDDED_CHECKOUT);
        if (embedRaw === "test" || embedRaw === "control") {
          analytics.register({ checkout_mode_variant: embedRaw });
        }
        setEmbeddedCheckout(embedRaw === "test");
      }
    };
    const unsubscribe = analytics.onFeatureFlags(decide);
    const timer = window.setTimeout(decide, 3000);
    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, []);

  if (funnel === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <img src={appIcon} alt="Tools for Procreate" className="w-12 h-12 rounded-2xl opacity-80" />
      </div>
    );
  }

  return funnel === "montage" ? (
    <MontageFunnel
      exitOfferEnabled={exitOfferEnabled}
      withQuiz={withQuiz}
      timerOffer={timerOffer}
      styleEmoji={styleEmoji}
      offerIntroArm={offerIntroArm}
      embeddedCheckout={embeddedCheckout}
    />
  ) : (
    <ClassicFunnel trialArm={trialArm} />
  );
};

export { Start };
